/* eslint-disable complexity */
import ToggleWeightCost from "@/components/toggleWeightCost/toggleWeightCost.vue";
import { Bar } from "vue-chartjs";
import moment from "moment";
import { REQUEST_STATE, STATUS_CODE } from "@/enums";
import { MenuItemsService } from "@/services/menuItemsApi";
import { formatNumberWithComma } from "@/utils";
import { createNamespacedHelpers } from "vuex";

const { mapGetters: userGetters } = createNamespacedHelpers("user"),
	{ mapGetters: filtersGetters } = createNamespacedHelpers("filters"),
	{ mapGetters: brandingGetters } = createNamespacedHelpers("branding");

export default {
	extends: Bar,
	components: { ToggleWeightCost },

	watch: {
		isCostToggleActive() {
			this.render();
		},
	},

	data() {
		return {
			isCostToggleActive: false,

			averageWastePerGuestData: [],
			state: REQUEST_STATE.LOADING,
			REQUEST_STATE,

			// https://codetribellc.atlassian.net/browse/KIT-713 - Improve chart options
			options: {
				title: { display: false },
				legend: { display: false },
				maintainAspectRatio: false,
				tooltips: {
					callbacks: {
						label: (context) => {
							const DECIMAL_PLACES = 2;
							const value = formatNumberWithComma(context.yLabel, DECIMAL_PLACES);
							return this.isCostToggleActive
								? `${this.activeOutlet.currency} ${value}`
								: `${value} ${this.weightUnitMinor}`;
						},
					},
				},
				scales: {
					xAxes: [
						{
							gridLines: { color: "rgba(0, 0, 0, 0)" },
							ticks: {
								fontFamily: "Nunito Sans",
								lineHeight: 1.3,
								precision: 0,
							},
						},
					],
					yAxes: [
						{
							ticks: {
								beginAtZero: true,
								fontFamily: "Nunito Sans",
								lineHeight: 1.3,
								padding: 10,
								precision: 0,
								maxTicksLimit: 6,
								stepSize: 0.1,
								// Include a dollar sign in the ticks
								callback: (value) => this.getChartYAxisLabel(value),
							},
						},
					],
				},
			},
		};
	},

	created() {
		this.loadData();
		this.$eventBus.$on("reloadComponentData", this.loadData);
	},

	beforeDestroy() {
		this.$eventBus.$off("reloadComponentData", this.loadData);
	},

	methods: {
		loadData,
		getAverageWastePerGuest,
		render,
		getChartLabels,
		getChartYAxisLabel,
	},

	computed: {
		...filtersGetters(["activePeriod", "activeDate"]),
		chartData,
		chartDatasetElementProperty,
		...userGetters(["activeOutlet", "weightUnitMinor", "weightFactorMinor"]),
		...brandingGetters(["colors"]),
	},
};

async function loadData() {
	await this.getAverageWastePerGuest();
	this.render();
}

async function getAverageWastePerGuest() {
	try {
		this.state = REQUEST_STATE.LOADING;
		const { data } = await MenuItemsService.getAverageWastePerGuestPerDay();

		this.averageWastePerGuestData = data.rows;
		this.state = REQUEST_STATE.SUCCESS;
	} catch (error) {
		if (error.response && error.response.status === STATUS_CODE.MISSING_DATA) this.state = REQUEST_STATE.MISSING_DATA;
	}
}

function render() {
	this.renderChart(this.chartData, this.options);
}

function chartData() {
	const columnChildrenCount = 7,
		accumulatedData = Array(columnChildrenCount)
			.fill(null)
			.map((val, index) => {
				let value = 0;

				/**
				 * check if backend is returning each day
				 * and not null for weight in order for graph to render
				 */
				const data = this.averageWastePerGuestData[index];

				if (this.getChartLabels()[index] !== this.$t("global.closed") && data && data.wpg)
					value = data[this.chartDatasetElementProperty];

				return this.isCostToggleActive ? value : value * this.weightFactorMinor;
			});

	return {
		labels: this.getChartLabels(),
		datasets: [
			{
				backgroundColor: this.colors[3],
				data: accumulatedData,
			},
		],
	};
}

function getChartLabels() {
	const showClosedLabel = ["day", "week"].includes(this.activePeriod),
		weekDayDates = this.$utils.getDateRange(
			moment(this.activeDate).startOf("isoWeek"),
			moment(this.activeDate).endOf("isoWeek")
		),
		labels = weekDayDates.map((date) => {
			if (showClosedLabel && this.$utils.isClosed(date)) return this.$t("global.closed");

			const formattedDate = this.$utils.replaceDoubleDotWithSingleDot(moment(date).format("ddd."));
			return this.$utils.capitalizeFirstLetter(formattedDate);
		});
	return labels;
}

function chartDatasetElementProperty() {
	return this.isCostToggleActive ? "cpg" : "wpg";
}

function getChartYAxisLabel(value) {
	// Calculate max value from graph data X axis to determine if we need decimal places for Y axis
	const maxValue = Math.max(...this.chartData.datasets[0].data);
	const decimalPlaces = maxValue < 1 && maxValue > 0 ? 1 : 0; // For values <1

	if (this.isCostToggleActive) return `${this.activeOutlet.currency} ${formatNumberWithComma(value, decimalPlaces)}`;

	return `${formatNumberWithComma(value, decimalPlaces)} ${this.weightUnitMinor}`;
}
